import { AxiosInstance } from 'axios';
import { KeycloakInstance } from 'keycloak-js';

/**
 * Setup axios interceptors to use Keycloak token
 *
 * @param axiosInstance - Axios instance to configure
 * @param keycloak - Keycloak instance
 */
export const setupAxiosKeycloak = (
  axiosInstance: AxiosInstance,
  keycloak: KeycloakInstance | null
) => {
  // Request interceptor
  axiosInstance.interceptors.request.use(
    async (config) => {
      if (keycloak && keycloak.authenticated) {
        // Update token if needed (will refresh if expired)
        try {
          await keycloak.updateToken(30); // Refresh if expires in 30 seconds

          // Add token to request
          if (keycloak.token) {
            config.headers.Authorization = `Bearer ${keycloak.token}`;
          }
        } catch (error) {
          console.error('Failed to update Keycloak token:', error);
          // Token refresh failed, user needs to login again
          keycloak.login();
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Prevent infinite retry loop
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // Unauthorized - token might be expired
        if (keycloak && keycloak.authenticated) {
          try {
            // Try to refresh token
            const refreshed = await keycloak.updateToken(-1); // Force refresh

            if (refreshed && keycloak.token) {
              // Retry the original request with new token
              originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
              return axiosInstance.request(originalRequest);
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // Refresh failed, redirect to login
            keycloak.login();
          }
        } else {
          // Not authenticated or fallback auth failed
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );
};
